import { action, decorate, observable, runInAction } from 'mobx';
import { ActionController, ActionType } from '@gp/common';

class BetBuilderStore {

    addRemoveErrorHandler;
    additionalOfferStores;
    isNextOddsEnabled;
    betBuilderService;
    betSlipStore;
    actionController;
    nextOddsOff = false;

    actionControllerOptions: {
        actionThrottle: 0,
        minorActionDelay: 4000,
    };

    constructor(
        {
            addRemoveErrorHandler,
            additionalOfferStores,
            isNextOddsEnabled = false,
            betBuilderService,
            betSlipStore,
        }
    ) {
        this.addRemoveErrorHandler = addRemoveErrorHandler;
        this.additionalOfferStores = additionalOfferStores;
        this.betBuilderService = betBuilderService;
        this.betSlipStore = betSlipStore;
        this.isNextOddsEnabled = isNextOddsEnabled;

        this.setNextOddsOff(!isNextOddsEnabled);

        this.betSlipStore.setHandleMultiOfferQuotaUpdates((eventIds) => this.updateMultiOfferQuotas(eventIds))
        this.actionController = new ActionController({
            functionCall: this.getNextOdds,
            ...this.actionControllerOptions,
        });
    }

    async getNextOdds(filter) {
        try {
            const response = await this.betBuilderService.getNextOdds(filter)

            const shouldResolveResponse = this.actionController.resolveAction(filter);

            if (!shouldResolveResponse) return null;

            if(this.isNextOddsEnabled){
                this.setNextOddsOff(Boolean(response.nextOddsOff));
            }

            if (response.oddsResponse && response.oddsResponse.length) {
                response.oddsResponse.forEach(or => {
                    this.updateBetBuilderAvailableTips(or.eventId, or.nextOdds);

                    const offer = this.betSlipStore.clickedTips.find(offer => offer.id.includes(or.eventId));

                    if (offer) {
                        if (offer.multiOffers.length === 1) {
                            offer.value = offer.multiOffers[0].value;
                        } else {
                            offer.value = or.betBuilderQuotaValue || 0;
                        }
                    } else {
                        this.clearAdditionalOfferStoreByEventId(or.eventId);
                    }
                });
            }
        } catch (error) {
            console.error("Get next odds request error:", error);
        }

        await this.betSlipStore.handleProcessBetSlip({ betSlipRequestData: () => this.betSlipStore.clickedTipsRequestModel, action: 0, type: "major" });
    }

    setAdditionalOfferStores(stores) {
        this.additionalOfferStores = stores;
    }

    addAdditionalOfferStore(store) {
        this.additionalOfferStores.push(store);
    }

    removeAdditionalOfferStore(event) {
        if (event.id != null) {
            const index = this.additionalOfferStores.findIndex(store => store.eventId === event.id || (store.event && store.event.id === event.id));
            if (index !== -1) {
                this.additionalOfferStores.splice(index, 1);
            }
        }
    }

    clearAdditionalOfferStoreByEventId(eventId) {
        const store = this.additionalOfferStores.find(store => store.eventId === eventId || (store.event && store.event.id === eventId));
        if (store) {
            store.betBuilderAvailableTips.clear();
        }
    }

    clearAdditionalOfferStores() {
        this.additionalOfferStores.forEach(store => {
            store.betBuilderAvailableTips.clear();
        });
    }

    setNextOddsOff(bool) {
        this.nextOddsOff = bool;
    }

    updateBetBuilderAvailableTips(eventId, nextOdds) {
        const store = this.additionalOfferStores.find(store => store.eventId === eventId);
        if (store) {
            store.setBetBuilderAvailableTips(nextOdds);
        }
    }

    onAdditionalStoreEnter(event) {
        const {
            id,
            liveStatus
        } = event;

        const isMultiOfferOnSlip = this.betSlipStore.isMultiOfferEventPresent(id);

        if (isMultiOfferOnSlip) {
            const multiOfferEvent = this.betSlipStore.clickedTips.find(offer => offer.eventId === id);

            if (multiOfferEvent) {
                const currentOffers = multiOfferEvent.multiOffers.map(e => e.multiOfferId);

                const filter = {
                    betBuilderOffers: [
                        {
                            eventId: id,
                            offerIds: [...currentOffers],
                            liveStatus: liveStatus,
                        }
                    ],
                    fetchNextOdds: true
                }

                this.actionController.handleNewAction({ ...filter, type: ActionType.IMMEDIATE_ACTION });
            }
        }
    }

    async removeBetBuilderEvent(eventId) {
        const isMultiOfferOnSlip = this.betSlipStore.isMultiOfferEventPresent(eventId);

        if (isMultiOfferOnSlip) {
            const index = this.betSlipStore.clickedTips.findIndex(ct => ct.id.includes(eventId));

            if (index != -1) {
                this.betSlipStore.clickedTips.splice(index, 1);

                this.clearAdditionalOfferStoreByEventId(eventId);

                if (this.betSlipStore.clickedTips.length > 0) {
                    await this.betSlipStore.handleProcessBetSlip({ betSlipRequestData: () => this.betSlipStore.clickedTipsRequestModel, action: 0, type: "major" });
                }
                else {
                    this.betSlipStore.reset();
                }
            }

        }
    }

    async updateQuotaAndSetAvailableOffers(tip, event) {
        const {
            eventId,
            id,
        } = tip

        const isMultiOfferOnSlip = this.betSlipStore.isMultiOfferEventPresent(eventId);
        const multiOfferEvent = isMultiOfferOnSlip && this.betSlipStore.clickedTips.find(offer => offer.eventId === eventId);
        const isRemove = multiOfferEvent ? multiOfferEvent.multiOffers.some(mo => mo.multiOfferId === id) : false;
        const currentOffers = multiOfferEvent ? multiOfferEvent.multiOffers.map(mo => mo.multiOfferId) : [];

        const processingTip = tip.multiOffers ? tip : { eventId: eventId, multiOffers: [{ multiOfferId: id, ...tip }] };

        if (isRemove) {
            let index = this.betSlipStore.clickedTips.findIndex(ct => ct.id.includes(eventId));

            if (index == -1) {
                console.error("Cant remove tip, not found");
                return;
            }

            const clickedTip = this.betSlipStore.clickedTips[index];
            clickedTip.multiOffers = clickedTip.multiOffers.filter(mo => !processingTip.multiOffers.some(newMo => newMo.multiOfferId === mo.multiOfferId));

            if (clickedTip.multiOffers.length < 1) {
                this.betSlipStore.clickedTips.splice(index, 1);
                this.clearAdditionalOfferStoreByEventId(eventId);
            }

            runInAction(() => { this.betSlipStore.clickedTips = [...this.betSlipStore.clickedTips]; });

            if (this.betSlipStore.clickedTips.length < 1) {
                this.betSlipStore.reset();
                if (this.additionalOfferStores.length > 0) {
                    this.clearAdditionalOfferStores();
                }
                return;
            }

            if (currentOffers.length === 1) return await this.betSlipStore.handleProcessBetSlip({ betSlipRequestData: () => this.betSlipStore.clickedTipsRequestModel, action: 0, type: "major" });
        } else {
            const errors = this.betSlipStore.addToBetSlipWithoutValidate(processingTip);
            if (errors) {
                this.addRemoveErrorHandler(errors, processingTip);
                return;
            }
        }

        const filter = {
            betBuilderOffers: [
                {
                    eventId: eventId,
                    offerIds: isRemove ? currentOffers.filter(offer => offer !== id) : [...currentOffers, id],
                    liveStatus: event.liveStatus,
                }],
            fetchNextOdds: true
        }

        this.actionController.handleNewAction({ ...filter, type: ActionType.IMMEDIATE_ACTION });
    }

    async updatePresetsQuotaAndSetAvailableOffers(tip, event) {

        const errors = this.betSlipStore.addToBetSlipWithoutValidate(tip);

        if (errors) {
            this.addRemoveErrorHandler(errors, tip);
            return;
        }

        const filter = {
            betBuilderOffers: [
                {
                    eventId: event.id,
                    offerIds: tip.multiOffers.map(offer => offer.multiOfferId),
                    liveStatus: event.liveStatus,
                }],
            fetchNextOdds: true
        }

        this.actionController.handleNewAction({ ...filter, type: ActionType.IMMEDIATE_ACTION });
    }

    updateMultiOfferQuotas(events) {

        const filter = events.reduce((acc, event) => {
            const offer = this.betSlipStore.clickedTips.find(offer => offer.eventId === event.id && offer.multiOffers);

            if (offer) {
                acc.betBuilderOffers.push({
                    eventId: event.id,
                    offerIds: offer.multiOffers.map(mo => mo.multiOfferId),
                    liveStatus: event.liveStatus,
                });
            }

            return acc;
        }, { betBuilderOffers: [], fetchNextOdds: true });


        if (filter.betBuilderOffers.length < 1) {
            return;
        }

        this.actionController.handleNewAction({ ...filter, type: ActionType.MINOR_ACTION });
    }
}

decorate(BetBuilderStore, {
    nextOddsOff: observable,
    getNextOdds: action.bound,
    setNextOddsOff: action.bound,
    setAdditionalOfferStores: action.bound,
    onAdditionalStoreEnter: action.bound,
    updateMultiOfferQuotas: action.bound,
    updateQuotaAndSetAvailableOffers: action.bound,
    updateBetBuilderAvailableTips: action.bound,
    clearAdditionalOfferStores: action.bound,
    addAdditionalOfferStore: action.bound,
    removeAdditionalOfferStore: action.bound,
    clearAdditionalOfferStoreByEventId: action.bound,
    removeBetBuilderEvent: action.bound,
});


export {
    BetBuilderStore
} 